import { OverlayModule } from '@angular/cdk/overlay';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { RouterModule } from '@angular/router';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { SharedUiMaterialModule } from '@digilize/shared/ui/material/src';
import { CurrencyMaskDirective, SharedUtilsDirectivesModule } from '@digilize/shared/utils/directives/src';
import { SharedUtilsPipesModule } from '@digilize/shared/utils/pipes/src';
import { TranslocoModule } from '@jsverse/transloco';
import { HighchartsChartModule } from 'highcharts-angular';
import { InlineSVGModule } from '@digilize/shared/ui/inline-svg/src';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { NgxCaptchaModule } from 'ngx-captcha';
import { ColorPickerDirective } from 'ngx-color-picker';
import { NgxGlideModule } from 'ngx-glide';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { SafePipeModule } from 'safe-pipe';

import { AbcRadioBtnComponent } from './abc-radio-btn/abc-radio-btn.component';
import { AccordionComponent } from './accordion/accordion.component';
import { AmountComponent } from './amount/amount.component';
import { CardSliderComponent } from './card-slider/card-slider.component';
import { CardAuctionBoardItemComponent } from './cards/card-auction-board-item/card-auction-board-item.component';
import { CardBroadcastComponent } from './cards/card-broadcast/card-broadcast.component';
import { CardCategoryComponent } from './cards/card-category/card-category.component';
import { CardEventComponent } from './cards/card-event/card-event.component';
import { CardOtherComponent } from './cards/card-other/card-other.component';
import { CardPhotoComponent } from './cards/card-photo/card-photo.component';
import { CardProductComponent } from './cards/card-product/card-product.component';
import { CardShopComponent } from './cards/card-shop/card-shop.component';
import { CheckboxComponent } from './checkbox/checkbox.component';
import { ChipsAutoComponent } from './chips-auto/chips-auto.component';
import { DateRangePickerComponent } from './date-range-picker/date-range-picker.component';
import { DatepickerWithHoursComponent } from './datepicker-with-hours/datepicker-with-hours.component';
import { DateTimeFieldComponent } from './date-time-field/date-time-field.component';
import { PaulyCheckboxComponent } from './form-items/pauly-checkbox/pauly-checkbox.component';
import { DatepickerComponent } from './datepicker/datepicker.component';
import { DialogConfirmComponent } from './dialog-confirm/dialog-confirm.component';
import { CodeFileImportDialogComponent } from './code-file-import-dialog/code-file-import-dialog.component';
import { DialogLayoutInnerXComponent } from './dialog-layouts/dialog-layout-inner-x/dialog-layout-inner-x.component';
import { DialogLayoutRadialComponent } from './dialog-layouts/dialog-layout-radial/dialog-layout-radial.component';
import { DndColumnWithArrowsComponent } from './dnd-column-with-arrows/dnd-column-with-arrows.component';
import { DndTableComponent } from './dnd-table/dnd-table.component';
import { DropdownContentComponent } from './dropdown-content/dropdown-content.component';
import { DropdownListComponent } from './dropdown-list/dropdown-list.component';
import { EventIconComponent } from './event-icon/event-icon.component';
import { FileUploadComponent } from './file-upload/file-upload.component';
import { FormAboutComponent } from './form-about/form-about.component';
import { FormAddressComponent } from './form-address/form-address.component';
import { FormCompanyLocationComponent } from './form-company-location/form-company-location.component';
import { FormContactComponent } from './form-contact/form-contact.component';
import { FormDeliveryComponent } from './form-delivery/form-delivery.component';
import { FormAmountInputComponent } from './form-amount-input/form-amount-input.component';
import { FaviconUploadComponent } from './favicon-upload/favicon-upload.component';
import { GtmContainerIdFieldComponent } from './gtm-container-id-field/gtm-container-id-field.component';
import { DialogGtmSnippetComponent } from './gtm-container-id-field/dialog-gtm-snippet/dialog-gtm-snippet.component';
import { FormHeaderComponent } from './form-header/form-header.component';
import { CustomSelectComponent } from './form-items/custom-select/custom-select.component';
import { InputComponent } from './form-items/input/input.component';
import { MultiSelectChipsCheckboxSearchComponent } from './form-items/multi-select-chips-checkbox-search/multi-select-chips-checkbox-search.component';
import { MultiSelectChipsExpandSearchComponent } from './form-items/multi-select-chips-expand-search/multi-select-chips-expand-search.component';
import { MultiSelectChipsSearchComponent } from './form-items/multi-select-chips-search/multi-select-chips-search.component';
import { MultiSelectComponent } from './form-items/multi-select/multi-select.component';
import { SelectCategoriesComponent } from './form-items/select-categories/select-categories.component';
import { SelectPlainComponent } from './form-items/select-plain/select-plain.component';
import { SelectSearchComponent } from './form-items/select-search/select-search.component';
import { SelectComponent } from './form-items/select/select.component';
import { ValidityPeriodComponent } from './form-items/validity-period/validity-period.component';
import { SingleSelectSearchComponent } from './form-items/single-select-search/single-select-search.component';
import { InsertFieldComponent } from './form-items/insert-field/insert-field.component';
import { TextEditorComponent } from './form-items/text-editor/text-editor.component';
import { FormKeywordsComponent } from './form-keywords/form-keywords.component';
import { FormPegiComponent } from './form-pegi/form-pegi.component';
import { FormPointsDivisionComponent } from './form-points-division/form-points-division.component';
import { FormPriceComponent } from './form-price/form-price.component';
import { FormQuizDatesComponent } from './form-quiz-dates/form-quiz-dates.component';
import { GenericTableComponent } from './generic-table/generic-table.component';
import { TableHeaderTemplateDirective, TableRowTemplateDirective } from './generic-table/generic-table.directive';
import { GroupedItemsListComponent } from './grouped-items-list/grouped-items-list.component';
import { HourInputComponent } from './hour-input/hour-input.component';
import { ImgRadioButtonComponent } from './img-radio-btn/img-radio-btn.component';
import { LoadingColumnAnimationComponent } from './loading-column-animation/loading-column-animation.component';
import { LoadingDotsAnimationComponent } from './loading-dots-animation/loading-dots-animation.component';
import { MobileOverlayComponent } from './mobile-overlay/mobile-overlay.component';
import { MovieFormatBadgesComponent } from './movie-format-badges/movie-format-badges.component';
import { MoviesSelectorComponent } from './movies-selector/movies-selector.component';
import { MultiAnswerTilesComponent } from './multi-answer-tiles/multi-answer-tiles.component';
import { MultiDropdownAutocompleteComponent } from './multi-dropdown-autocomplete/multi-dropdown-autocomplete.component';
import { MultipleRadioInputsComponent } from './multiple-radio-inputs/multiple-radio-inputs.component';
import { NestedDropdownComponent } from './nested-dropdown/nested-dropdown.component';
import { NotFoundWithImageComponent } from './not-found-with-image/not-found-with-image.component';
import { OrderDndControlComponent } from './order-dnd-control/order-dnd-control.component';
import { PhotoUploadListComponent } from './photo-upload-list/photo-upload-list.component';
import { ProfileCircleImgComponent } from './profile-circle-img/profile-circle-img.component';
import { ProgressBarTextComponent } from './progress-bar-text/progress-bar-text.component';
import { ProgressBarComponent } from './progress-bar/progress-bar.component';
import { RespondentListComponent } from './respondent-list/respondent-list.component';
import { SearchSelectComponent } from './search-select/search-select.component';
import { SingleAnswerTilesComponent } from './single-answer-tiles/single-answer-tiles.component';
import { SmileInputComponent } from './smile-input/smile-input.component';
import { SpeakerBtnComponent } from './speaker-btn/speaker-btn.component';
import { StarsRateComponent } from './stars-rate/stars-rate.component';
import { StepBtnsComponent } from './step-btns/step-btns.component';
import { SummaryItemComponent } from './summary-item/summary-item.component';
import { TileRadioBtnComponent } from './tile-radio-btn/tile-radio-btn.component';
import { TypeSelectorComponent } from './type-selector/type-selector.component';
import { TimeCaruselComponent } from './time-carusel/time-carusel.component';
import { TimePickerComponent } from './time-picker/time-picker.component';
import { VirtualScrollTableComponent } from './virtual-scroll-table/virtual-scroll-table.component';
import {
  VirtualTableHeaderTemplateDirective,
  VirtualTableRowTemplateDirective,
} from './virtual-scroll-table/virtual-scroll-table.directive';

// import { FormScanComponent } from './form-scan/form-scan.component';
// import { FormIntegrationComponent } from './form-integration/form-integration.component';
const DIRECTIVES = [
  TableRowTemplateDirective,
  TableHeaderTemplateDirective,
  VirtualTableHeaderTemplateDirective,
  VirtualTableRowTemplateDirective,
];
const COMPONENTS = [
  SelectComponent,
  ValidityPeriodComponent,
  SelectPlainComponent,
  SelectCategoriesComponent,
  TextEditorComponent,
  InsertFieldComponent,
  SummaryItemComponent,
  SelectSearchComponent,
  MultiSelectComponent,
  EventIconComponent,
  FormAboutComponent,
  FormAddressComponent,
  FormAmountInputComponent,
  FormCompanyLocationComponent,
  FormContactComponent,
  FormDeliveryComponent,
  FormHeaderComponent,
  FormKeywordsComponent,
  FormPegiComponent,
  FormPriceComponent,
  FormQuizDatesComponent,
  // FormScanComponent,
  ProgressBarComponent,
  SingleAnswerTilesComponent,
  SmileInputComponent,
  MultiAnswerTilesComponent,
  MultipleRadioInputsComponent,
  NestedDropdownComponent,
  DropdownListComponent,
  CardSliderComponent,
  CardAuctionBoardItemComponent,
  CardBroadcastComponent,
  CardCategoryComponent,
  CardEventComponent,
  CardOtherComponent,
  CardPhotoComponent,
  CardProductComponent,
  CardShopComponent,
  ChipsAutoComponent,
  TileRadioBtnComponent,
  TypeSelectorComponent,
  TimePickerComponent,
  GenericTableComponent,
  VirtualScrollTableComponent,
  MultiDropdownAutocompleteComponent,
  RespondentListComponent,
  LoadingColumnAnimationComponent,
  ProgressBarTextComponent,
  CheckboxComponent,
  SingleSelectSearchComponent,
  FileUploadComponent,
  MultiSelectChipsSearchComponent,
  MultiSelectChipsCheckboxSearchComponent,
  MobileOverlayComponent,
  ImgRadioButtonComponent,
  AccordionComponent,
  LoadingDotsAnimationComponent,
  AbcRadioBtnComponent,
  OrderDndControlComponent,
  AmountComponent,
  DialogLayoutInnerXComponent,
  DialogLayoutRadialComponent,
  DropdownContentComponent,
  StarsRateComponent,
  NotFoundWithImageComponent,
  ProfileCircleImgComponent,
  SpeakerBtnComponent,
  MoviesSelectorComponent,
  FormPointsDivisionComponent,
  DndColumnWithArrowsComponent,
  HourInputComponent,
  CustomSelectComponent,
  InputComponent,
  DndTableComponent,
  PhotoUploadListComponent,
  GroupedItemsListComponent,
  DialogConfirmComponent,
  CodeFileImportDialogComponent,
  SearchSelectComponent,
  StepBtnsComponent,
  DatepickerComponent,
  // FormIntegrationComponent,
  DateRangePickerComponent,
  MultiSelectChipsExpandSearchComponent,
  TimeCaruselComponent,
  DatepickerWithHoursComponent,
  DateTimeFieldComponent,
  PaulyCheckboxComponent,
  FaviconUploadComponent,
  GtmContainerIdFieldComponent,
  DialogGtmSnippetComponent,
];
// Standalone, so the webshop and the portal can import them without this whole module.
const STANDALONE_COMPONENTS = [MovieFormatBadgesComponent];
@NgModule({
  declarations: [...COMPONENTS, ...DIRECTIVES],
  exports: [...COMPONENTS, ...DIRECTIVES, ...STANDALONE_COMPONENTS],
  imports: [
    CommonModule,
    LazyLoadImageModule,
    NgxGlideModule,
    InlineSVGModule,
    NgScrollbarModule,
    FormsModule,
    ReactiveFormsModule,
    SharedUtilsDirectivesModule,
    SharedUiMaterialModule,
    CKEditorModule,
    SafePipeModule,
    NgxCaptchaModule,
    SharedUtilsPipesModule,
    ColorPickerDirective,
    CurrencyMaskDirective,
    RouterModule,
    TranslocoModule,
    HighchartsChartModule,
    MatExpansionModule,
    MatDatepickerModule,
    MatButtonModule,
    MatInputModule,
    MatIconModule,
    ScrollingModule,
    OverlayModule,
    MatChipsModule,
    ...STANDALONE_COMPONENTS,
  ],
  providers: [provideHttpClient(withXhr(), withInterceptorsFromDi())],
})
export class SharedUiComponentsModule {}
